import { ApiError, handleApiError } from '@/handleApiError'
import api from '@/modules/product/api'
import useBundleItems from '@/modules/product/useBundleItems'
import useProductQuantity from '@/modules/product/useProductQuantity'
import useRealtimePrices from '@/modules/product/useRealtimePrices'
import useTopi from '@/modules/topi/useTopi'
import ProductPageEvent from '@/modules/tracking/events/productPage'
import { trackEvent } from '@/modules/tracking/useTracking'
import { t } from '@/plugins/i18n.ts'
import { FullProduct, ProductPriceUpdate } from '@/types/index'
import { defineStore } from 'pinia'

interface ProductState {
  product: FullProduct | undefined
  loading: boolean
}

const initialState = (): ProductState => {
  return {
    product: undefined,
    loading: false
  }
}

const useProduct = defineStore('product', {
  state: () => initialState(),
  actions: {
    async loadProduct(id: number) {
      this.$reset()
      this.loading = true
      useProductQuantity().$reset()
      useBundleItems().$reset()

      return api
        .loadProduct(id)
        .then(({ data }) => {
          this.product = data
          trackEvent(ProductPageEvent.PageView(data.title))
          useRealtimePrices().loadRealtimePrices({ productId: id, scale: 1 })
          if (this.product.isBundle) {
            useBundleItems().loadBundleItems(id)
          }
          useProductQuantity().initializeQuantity(this.product.price || undefined)
        })
        .catch((error: ApiError) => {
          handleApiError(
            error,
            {
              appearance: 'CUSTOM',
              customHandler: 'CategoryLayout'
            },
            {
              errorCode: 404,
              logError: false,
              customMessage: t('error.productNotFound')
            }
          )
        })
        .finally(() => {
          this.loading = false
        })
    },
    updateProductPrices(update: ProductPriceUpdate) {
      if (this.product) {
        this.product.price = update.price
        this.product.stock = update.stock
        this.product.suppliers = update.suppliers
        useTopi().setPdpItem(this.product)
      }
    }
  }
})

export default useProduct
